/**
 * PostHog's project key (`phc_*`): write-only, submits events, and cannot read
 * anything back, so it ships to browsers by design. Not to be confused with a
 * personal API key (`phx_*`), which reads data and is a real secret — this one
 * belongs in source, not Doppler.
 *
 * Lives here rather than in `@replohq/bedrock-sdk` so edge workers can reach it
 * without depending on the SDK: `worker-utils` sits below the SDK in the build
 * graph, and importing it there closes a turbo dependency cycle.
 */
export const POSTHOG_PUBLIC_KEY =
  "phc_dRETsj85vNkKrvQQszEFRBibH5kUF9l7rbjACuKPanZ";
export const POSTHOG_API_HOST = "https://d99bbtllqsaif.cloudfront.net";
export const POSTHOG_CAPTURE_URL = "https://us.i.posthog.com/capture";

/**
 * Standardized group names we use for analytics (e.g. posthog). Important to
 * keep these in a standardized place, because passing the exact same string is
 * important across the backend and frontend to make sure that .group() calls in
 * the frontend line up with the group names we pass in the backend.
 */
export const ANALYTICS_GROUP_NAMES = {
  project: "project",
  /**
   * Holdover from when we used to use the domain on the user email address as a way to do orgs.
   *
   * @deprecated
   * @author Ben 2024-08-16
   */
  domain: "organization",
  workspace: "workspace",
} as const;

/**
 * PostHog assigns each group type a fixed numeric index when it is first
 * created, and its unique-group insights read the indexed `$group_N` event
 * property rather than the named `$groups` object. The posthog-js / posthog-node
 * SDKs expand `$groups` into `$group_N` on the client's behalf, but our Workers
 * capture over raw HTTP (no SDK), so they must stamp `$group_N` themselves —
 * verified in production that raw-captured events otherwise carried `$groups`
 * with 0% `$group_2`. Indexes verified 2026-07-21 against live client events and
 * never change once assigned. (Ezra, REPL-29399)
 */
export const ANALYTICS_GROUP_TYPE_INDEXES = {
  [ANALYTICS_GROUP_NAMES.project]: 2,
  [ANALYTICS_GROUP_NAMES.workspace]: 4,
} as const;

/**
 * Builds the group properties a raw-HTTP PostHog capture needs, stamping both
 * the named `$groups` object and the indexed `$group_N` properties.
 */
export function buildAnalyticsGroupProperties({
  projectId,
  workspaceId,
}: {
  projectId?: string | null;
  workspaceId?: string | null;
}): Record<string, unknown> {
  const groups: Record<string, string> = {};
  const indexed: Record<string, string> = {};
  if (projectId) {
    groups[ANALYTICS_GROUP_NAMES.project] = projectId;
    indexed[
      `$group_${ANALYTICS_GROUP_TYPE_INDEXES[ANALYTICS_GROUP_NAMES.project].toString()}`
    ] = projectId;
  }
  if (workspaceId) {
    groups[ANALYTICS_GROUP_NAMES.workspace] = workspaceId;
    indexed[
      `$group_${ANALYTICS_GROUP_TYPE_INDEXES[ANALYTICS_GROUP_NAMES.workspace].toString()}`
    ] = workspaceId;
  }
  return { $groups: groups, ...indexed };
}
